/**
 * Impact Assessment Checklist Form
 * Shared form for Create / Edit Impact Assessment (plan name, concepts, keywords).
 * Used by Configure (FilterBar) and Edit (HomePage) dialogs.
 */

import React from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import type {
  ChecklistConceptItem,
  ImpactAssessmentChecklistFormValue,
} from "./types";
import {
  ConceptsSelector,
  type ConceptsSelectorVariant,
} from "./ConceptsSelector";

const NUNITO = '"Nunito Sans", sans-serif' as const;

/** Figma 244:10986 — outlined inputs on white. */
const PLAN_DIALOG_FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    bgcolor: "#FFFFFF",
    fontFamily: NUNITO,
    fontSize: 14,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#E2E8F0",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#E2E8F0",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "primary.main",
    borderWidth: "1px",
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#6B7280",
    opacity: 1,
  },
} as const;

function TotalCountBadge({
  current,
  max,
}: Readonly<{ current: number; max: number }>) {
  return (
    <Box
      sx={{
        px: "9px",
        py: "5px",
        borderRadius: "4px",
        bgcolor: "#F1F5F9",
        border: "1px solid #E2E8F0",
        flexShrink: 0,
      }}
    >
      <Typography
        sx={{
          fontFamily: NUNITO,
          fontWeight: 500,
          fontSize: 12,
          lineHeight: "16px",
          color: "#475569",
        }}
      >
        {current} / {max} total
      </Typography>
    </Box>
  );
}

export interface ImpactAssessmentChecklistFormProps {
  value: ImpactAssessmentChecklistFormValue;
  onChange: (value: ImpactAssessmentChecklistFormValue) => void;
  /** Mock concepts list; when undefined, uses API (values/search) */
  conceptsList?: ChecklistConceptItem[];
  /** Maximum total selections across concepts + keywords (fallback 25). */
  maxTotalSelections?: number;
  /** Maximum length per keyword (fallback 80). */
  maxKeywordLength?: number;
  /**
   * Concepts picker layout/skin. `processMapping` matches Assessment Center process-mapping styling.
   * Default `standard` (Configure checklist, Home auto-assessment, etc.).
   */
  conceptsSelectorVariant?: ConceptsSelectorVariant;
  /** Description field placeholder (default references checklist; plan dialogs may override). */
  descriptionPlaceholder?: string;
}

export const ImpactAssessmentChecklistForm: React.FC<
  ImpactAssessmentChecklistFormProps
> = ({
  value,
  onChange,
  conceptsList,
  maxTotalSelections,
  maxKeywordLength,
  conceptsSelectorVariant = "standard",
  descriptionPlaceholder = "Brief description of this checklist...",
}) => {
  const isPlanDialogSkin = conceptsSelectorVariant === "processMapping";
  const selectedCount = new Set(value.selectedConceptIds).size;
  const keywordCount = value.keywords.trim()
    ? value.keywords
        .trim()
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean).length
    : 0;
  const totalCount = selectedCount + keywordCount;
  const maxTotal = typeof maxTotalSelections === "number" ? maxTotalSelections : 25;
  const maxKeywordLengthByConfig =
    typeof maxKeywordLength === "number" ? maxKeywordLength : 80;

  const labelPrimarySx = isPlanDialogSkin
    ? {
        fontFamily: NUNITO,
        fontWeight: 600,
        fontSize: 14,
        lineHeight: "20px",
        color: "#334155",
      }
    : { display: "block" as const };

  const counterSx = isPlanDialogSkin
    ? {
        fontFamily: NUNITO,
        fontSize: 12,
        lineHeight: "16px",
        color: "#64748B",
      }
    : undefined;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: isPlanDialogSkin ? 4 : 2,
      }}
    >
      {/* Plan name & description */}
      <Box
        sx={
          isPlanDialogSkin
            ? {
                display: "flex",
                flexDirection: "column",
                gap: 3,
                p: 0,
                bgcolor: "transparent",
              }
            : {
                p: 2,
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                bgcolor: "grey.50",
              }
        }
      >
        <Box sx={{ mb: isPlanDialogSkin ? 0 : 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
              mb: 1,
            }}
          >
            <Typography
              component="label"
              variant="body2"
              fontWeight={isPlanDialogSkin ? 600 : 500}
              sx={labelPrimarySx}
            >
              Plan Name <span style={{ color: "#EF4444" }}>*</span>
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={counterSx}>
              {value.name.length}/20
            </Typography>
          </Box>
          <TextField
            id="plan-name"
            size="small"
            fullWidth
            placeholder="e.g., Standard Biologics Assessment"
            value={value.name}
            onChange={(e) => {
              const val = e.target.value;
              if (val.length <= 20) {
                onChange({ ...value, name: val });
              }
            }}
            sx={{
              "& .MuiInputBase-root": { bgcolor: "background.paper" },
              ...(isPlanDialogSkin ? PLAN_DIALOG_FIELD_SX : {}),
            }}
          />
        </Box>
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
              mb: 1,
            }}
          >
            <Typography
              component="label"
              variant="body2"
              fontWeight={isPlanDialogSkin ? 400 : 500}
              sx={
                isPlanDialogSkin
                  ? {
                      fontFamily: NUNITO,
                      fontSize: 14,
                      lineHeight: "20px",
                      color: "#334155",
                    }
                  : { display: "block" }
              }
            >
              Description (Optional)
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={counterSx}>
              {(value.description || "").length}/50
            </Typography>
          </Box>
          <TextField
            id="checklist-description"
            size="small"
            fullWidth
            multiline
            minRows={isPlanDialogSkin ? 3 : 2}
            placeholder={descriptionPlaceholder}
            value={value.description}
            onChange={(e) => {
              const val = e.target.value;
              if (val.length <= 50) {
                onChange({ ...value, description: val });
              }
            }}
            sx={{
              "& .MuiInputBase-root": { bgcolor: "background.paper" },
              ...(isPlanDialogSkin ? PLAN_DIALOG_FIELD_SX : {}),
            }}
          />
        </Box>
      </Box>

      {/* Select Concepts */}
      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            mb: isPlanDialogSkin ? 2 : 1,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              sx={
                isPlanDialogSkin
                  ? {
                      fontFamily: NUNITO,
                      fontSize: 14,
                      lineHeight: "20px",
                      color: "#334155",
                    }
                  : undefined
              }
            >
              Select Values
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "block",
                ...(isPlanDialogSkin
                  ? {
                      fontFamily: NUNITO,
                      fontSize: 12,
                      lineHeight: "16px",
                      color: "#64748B",
                    }
                  : {}),
              }}
            >
              Select at least one and at most {maxTotal} values
            </Typography>
          </Box>
          {isPlanDialogSkin ? (
            <TotalCountBadge current={totalCount} max={maxTotal} />
          ) : (
            <Chip label={`${totalCount} / ${maxTotal} total`} size="small" />
          )}
        </Box>
        <ConceptsSelector
          conceptsList={conceptsList}
          value={{
            selectedConceptIds: value.selectedConceptIds,
            selectedConceptsDetails: value.selectedConceptsDetails,
          }}
          onChange={(next) =>
            onChange({
              ...value,
              selectedConceptIds: next.selectedConceptIds,
              selectedConceptsDetails: next.selectedConceptsDetails,
            })
          }
          maxTotalSelections={maxTotal}
          totalSelectionsCount={totalCount}
          variant={conceptsSelectorVariant}
        />
      </Box>

      {/* Keywords */}
      <Box
        sx={
          isPlanDialogSkin
            ? { p: 0, bgcolor: "transparent" }
            : { p: 2, border: 1, borderColor: "divider", borderRadius: 1 }
        }
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            mb: isPlanDialogSkin ? 2 : 0,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <Typography
              component="label"
              variant="subtitle2"
              fontWeight={600}
              sx={
                isPlanDialogSkin
                  ? {
                      fontFamily: NUNITO,
                      fontSize: 14,
                      lineHeight: "20px",
                      color: "#334155",
                    }
                  : { display: "block", mb: 0.5 }
              }
            >
              Keywords
            </Typography>
            {isPlanDialogSkin ? (
              <Typography
                sx={{
                  fontFamily: NUNITO,
                  fontSize: 12,
                  lineHeight: "16px",
                  color: "#64748B",
                }}
              >
                Enter keywords separated by commas
              </Typography>
            ) : null}
          </Box>
          {isPlanDialogSkin ? (
            <TotalCountBadge current={keywordCount} max={maxTotal} />
          ) : null}
        </Box>
        <TextField
          id="keywords"
          size="small"
          fullWidth
          multiline
          minRows={3}
          placeholder={
            isPlanDialogSkin
              ? "e.g. keywords1, keywords2..."
              : "Enter keywords separated by commas"
          }
          value={value.keywords}
          onChange={(e) => {
            const raw = e.target.value;
            const parts = raw
              .trim()
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean);
            const maxKeywords = Math.max(0, maxTotal - selectedCount);
            const exceedsSingleKeywordLength = parts.some(
              (item) => item.length > maxKeywordLengthByConfig,
            );
            const normalized = parts
              .map((item) => item.slice(0, maxKeywordLengthByConfig))
              .slice(0, maxKeywords)
              .join(", ");
            const truncated =
              parts.length > maxKeywords || exceedsSingleKeywordLength
                ? normalized
                : raw;
            onChange({ ...value, keywords: truncated });
          }}
          helperText={
            isPlanDialogSkin
              ? undefined
              : `${keywordCount} keywords (1–${maxTotal} total with values)`
          }
          sx={{
            "& .MuiInputBase-root": { bgcolor: "background.paper" },
            ...(isPlanDialogSkin ? PLAN_DIALOG_FIELD_SX : {}),
          }}
        />
        {isPlanDialogSkin ? (
          <Typography
            sx={{
              fontFamily: NUNITO,
              fontSize: 12,
              lineHeight: "16px",
              color: "#64748B",
              mt: 1,
            }}
          >
            {keywordCount} keywords (1–{maxTotal} total with values)
          </Typography>
        ) : null}
      </Box>

      <Alert
        severity="info"
        icon={<InfoOutlinedIcon />}
        sx={
          isPlanDialogSkin
            ? {
                bgcolor: "#F8FAFC",
                border: "1px solid #E2E8F0",
                color: "#475569",
                "& .MuiAlert-icon": { color: "#64748B" },
                fontFamily: NUNITO,
                fontSize: 14,
                lineHeight: "20px",
              }
            : { bgcolor: "rgba(25, 118, 210, 0.08)" }
        }
      >
        <strong>Tip:</strong>Choose at least one value from the four dimensions or enter at
        least one keyword to proceed.
      </Alert>
    </Box>
  );
};
